import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { InplaceZoom } from '@theme/inplace-zoom';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [rail] - The desktop thumbnail rail (C'est Interdit patch).
 * @property {HTMLElement[]} [railThumbs] - The thumbnails in the rail.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.refs.rail) {
      // Captured on document: scroll doesn't bubble, and the page may scroll an element, not the window.
      document.addEventListener('scroll', this.#queueRailUpdate, { capture: true, passive: true, signal });
      window.addEventListener('resize', this.#queueRailUpdate, { passive: true, signal });
      this.#queueRailUpdate();
    }
  }

  #controller = new AbortController();

  /** In-place photo zoom for mouse users (C'est Interdit patch). */
  #inplaceZoom = new InplaceZoom();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#inplaceZoom.exit();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    // C'est Interdit patch: with a mouse, zoom the photo in its own frame instead of the full-screen dialog.
    if (this.#inplaceZoom.toggle(this.refs.media?.[index], event)) return;

    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    // The dialog isn't used with a mouse (C'est Interdit patch), so don't fetch its 3840px photo.
    if (InplaceZoom.supported) return;

    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * Scrolls the page to a grid item. Called by the thumbnail rail (C'est Interdit patch).
   *
   * @param {number} index - The index of the media to scroll to.
   */
  scrollToMedia(index) {
    const item = this.refs.media?.[index];
    if (!item) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    item.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  #railFrame = 0;

  #queueRailUpdate = () => {
    if (this.#railFrame) return;
    this.#railFrame = requestAnimationFrame(() => {
      this.#railFrame = 0;
      this.#updateRail();
    });
  };

  /**
   * Marks the thumbnails of the grid items in view, and keeps the first of them visible inside the
   * rail by scrolling the rail only, never the page.
   */
  #updateRail() {
    const { rail, railThumbs = [], media = [] } = this.refs;
    if (!rail || rail.offsetParent === null) return;

    const top = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const bottom = window.innerHeight;

    // An item counts as "in view" when at least half of what could fit on screen is showing.
    const shown = media.map((item) => {
      const rect = item.getBoundingClientRect();
      const visible = Math.max(0, Math.min(rect.bottom, bottom) - Math.max(rect.top, top));
      return visible / Math.max(1, Math.min(rect.height, bottom - top));
    });

    let active = shown.map((fraction) => fraction >= 0.5);
    if (!active.includes(true)) {
      const best = shown.indexOf(Math.max(...shown));
      active = shown.map((_, index) => index === best);
    }

    railThumbs.forEach((thumb, index) => {
      if (active[index]) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    // Hold the wheel only when the rail has something to scroll; otherwise it would trap the page.
    const overflowing = rail.scrollHeight > rail.clientHeight;
    rail.toggleAttribute('data-overflowing', overflowing);
    if (!overflowing) return;

    // Keep the whole active group in view if it fits, else its first thumb.
    const first = railThumbs[active.indexOf(true)];
    const last = railThumbs[active.lastIndexOf(true)];
    if (!first || !last) return;

    const groupTop = first.offsetTop;
    const groupBottom = Math.min(last.offsetTop + last.offsetHeight, groupTop + rail.clientHeight);
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    if (groupTop < rail.scrollTop) {
      rail.scrollTo({ top: groupTop, behavior });
    } else if (groupBottom > rail.scrollTop + rail.clientHeight) {
      rail.scrollTo({ top: groupBottom - rail.clientHeight, behavior });
    }
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
